import React, { useEffect, useMemo, useState } from 'react';

import { BsPlusLg } from 'react-icons/bs';
import { TbDeviceMobile, TbHandMove, TbLayoutList } from 'react-icons/tb';
import CollectionModal from './CollectionModal';
import { HomeQuery } from 'queries';
import useModal from 'hooks/useModal';
import { ICollection } from 'types';
import CollectionList from './CollectionList';
import CollectionItem from './CollectionItem';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { ModalComponent, UiComponent } from 'components';
import { useIsMobile } from 'hooks';
import Preview from './Preview';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import {
  header,
  dirtyBadge,
  layout,
  previewCard,
  cardHead,
  phone,
  phoneScreen,
  phoneEmpty,
  listCard,
  empty,
  hint,
  headerActions,
  previewSheet,
} from './style';

function Manage() {
  const isMobile = useIsMobile();
  const { handleModal, handleClose } = useModal();
  const [isOpenPreview, setIsOpenPreview] = useState(false);
  const [collections, setCollections] = useState<ICollection[]>([]);
  const [savedIds, setSavedIds] = useState<number[]>([]);

  const handleDrop = (item: { collection: ICollection }, index: number) => {
    setCollections((prev) => {
      const filtered = prev.filter((c) => c.id !== item.collection.id);
      return [
        ...filtered.slice(0, index),
        item.collection,
        ...filtered.slice(index),
      ];
    });
  };

  const { mutate } = HomeQuery.useUpdate();
  const { data } = HomeQuery.useGet();

  const handleManageCollection = () => {
    if (!data) return;

    handleModal(
      '컬렉션 추가',
      <CollectionModal
        collectionIds={collections.map((c: ICollection) => c.id)}
        handleSelect={handleSelect}
      />,
    );
  };

  const handleSelect = (collection: ICollection) => {
    if (collections.find((c) => c.id === collection.id)) {
      toast.error('이미 되어있습니다.');
      handleClose();
      return;
    }

    setCollections((prev) => [...prev, collection]);
    handleClose();
    toast.success('추가되었습니다.');
  };

  const handleRemove = (id: number) => {
    setCollections((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSave = () => {
    if (!data) return;

    const ids = collections.map((c) => c.id);
    mutate(ids, {
      onSuccess: () => {
        setSavedIds(ids);
        toast.success('저장되었습니다.');
      },
      onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
    });
  };

  useEffect(() => {
    if (!data) return;
    const order = data.data.collectionOrder.map((o: string) => Number(o));
    const sortedCollections = [...data.data.collections].sort((a, b) => {
      const indexA = order.indexOf(a.id);
      const indexB = order.indexOf(b.id);
      return indexA - indexB;
    });
    setCollections(sortedCollections);
    setSavedIds(sortedCollections.map((c: ICollection) => c.id));
  }, [data]);

  const isDirty = useMemo(
    () => collections.map((c) => c.id).join(',') !== savedIds.join(','),
    [collections, savedIds],
  );

  const previewScreen = (
    <div css={phoneScreen}>
      {collections.length === 0 ? (
        <div css={phoneEmpty}>
          <TbLayoutList size={28} />
          노출할 컬렉션이 없어요
        </div>
      ) : (
        collections.map((c) => (
          <div key={c.id}>
            <Preview collection={c} />
          </div>
        ))
      )}
    </div>
  );

  return (
    <>
      <div css={header}>
        <div>
          <UiComponent.PageTitle>홈 화면 관리</UiComponent.PageTitle>
          {data && (
            <span css={dirtyBadge(isDirty)}>
              {isDirty ? '저장되지 않은 변경사항' : '저장됨'}
            </span>
          )}
        </div>
        <div css={headerActions}>
          {isMobile && (
            <UiComponent.Button
              color="grey"
              variant="border"
              onClick={() => setIsOpenPreview(true)}
              css={{ height: '40px' }}
            >
              <TbDeviceMobile size={16} />
              미리보기
            </UiComponent.Button>
          )}
          <UiComponent.Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            css={{ height: '40px', minWidth: '96px' }}
          >
            저장하기
          </UiComponent.Button>
        </div>
      </div>

      <div css={layout}>
        {!isMobile && (
          <section css={previewCard}>
            <div css={cardHead}>
              <h4>
                <TbDeviceMobile size={18} />앱 미리보기
              </h4>
              <p>저장 전 화면이에요</p>
            </div>
            <div css={phone}>{previewScreen}</div>
          </section>
        )}

        <section css={listCard}>
          <div css={cardHead}>
            <h4>
              노출 컬렉션 <small>{collections.length}</small>
            </h4>
            <UiComponent.Button
              color="primary"
              variant="contained"
              onClick={handleManageCollection}
              css={{ height: '36px' }}
            >
              <BsPlusLg size={14} />
              컬렉션 추가
            </UiComponent.Button>
          </div>

          {collections.length === 0 ? (
            <div css={empty}>
              <TbLayoutList size={32} />
              <div>
                <strong>홈 화면에 노출할 컬렉션을 추가해주세요</strong>
                추가한 순서대로 앱 홈 화면에 보여요.
              </div>
              <UiComponent.Button
                color="background"
                variant="contained"
                onClick={handleManageCollection}
              >
                <BsPlusLg size={14} />
                컬렉션 추가
              </UiComponent.Button>
            </div>
          ) : (
            <>
              <p css={hint}>
                <TbHandMove size={16} />
                드래그해서 노출 순서를 바꿀 수 있어요
              </p>
              <DndProvider backend={HTML5Backend}>
                <CollectionList onDrop={handleDrop}>
                  {collections.map((c, i) => (
                    <CollectionItem
                      key={c.id}
                      order={i + 1}
                      collection={c}
                      handleRemove={handleRemove}
                    />
                  ))}
                </CollectionList>
              </DndProvider>
            </>
          )}
        </section>
      </div>

      {isOpenPreview && (
        <ModalComponent.Portal>
          <ModalComponent.Background>
            <ModalComponent.Modal
              title="앱 미리보기"
              handleClose={() => setIsOpenPreview(false)}
              footer={<></>}
            >
              <div css={previewSheet}>{previewScreen}</div>
            </ModalComponent.Modal>
          </ModalComponent.Background>
        </ModalComponent.Portal>
      )}
    </>
  );
}

export default Manage;
